import { useEffect, type ReactNode } from 'react';

import { cn } from '@shared/ui/cn';

/**
 * The one composite both system surfaces render, and the only thing they share.
 *
 * The design system names the **three parts** of an error state — what happened,
 * what was unaffected, what the reader does now — and names no component and no
 * class to carry them. So the block is a data attribute and its kind rides the
 * attribute's own value, exactly as the pack's other unnamed shapes do; there is
 * no invented modifier class and no new colour.
 *
 * Three rules the pair establishes for the whole area, and all three live here:
 *
 * - **No status numeral anywhere in the interface.** An error states the remedy;
 *   the code goes to the log. The two pages are told apart by their icon, their
 *   text, and their actions — never by a number on screen. `404` and `500` are
 *   the fall-through names the ingress serves them under, which is a deployment
 *   fact rather than a string the operator reads.
 * - **No page head above the `<h1>`.** The heading *is* the error statement, and
 *   a text head over it would put two first-level headings in one document.
 * - **No `role="alert"`.** This is not a status message arriving inside a working
 *   page — it is a page whose entire content is the error, present at load and
 *   announced once through the document title and the heading. Wrapping it in an
 *   assertive live region would interrupt a reader to tell them what they are
 *   already being read.
 *
 * The block fills the height the content column was given and centres in it,
 * rather than sitting against the top bar over empty space, which reads as a
 * half-loaded page.
 */
export function ErrorPage({
  kind,
  documentTitle,
  icon,
  heading,
  body,
  actions,
}: {
  /** `notfound` is an absence and stays muted; `server` is a fault and takes the failure colour. */
  kind: 'notfound' | 'server';
  documentTitle: string;
  icon: ReactNode;
  heading: string;
  body: string;
  actions: ReactNode;
}) {
  useDocumentTitle(documentTitle);

  return (
    <section
      data-error={kind}
      className="grid h-full max-w-measure content-center justify-items-start gap-stack-normal py-8 md:py-9 lg:gap-stack-loose"
    >
      {/*
        The status mark sits in a frame raised one level off the page so it reads
        as a state marker rather than as decoration. `notfound` keeps the muted
        ink — an absence is not a fault, and the failure colour is reserved for
        failure. `server` takes it, and colour is never the only signal: the
        heading and the paragraph both say what happened in words.
      */}
      <span
        data-error-icon
        aria-hidden="true"
        className={cn(
          'inline-grid size-8 place-items-center rounded-md border md:size-[3.5rem]',
          '[&>svg]:size-5 md:[&>svg]:size-[1.75rem]',
          kind === 'server'
            ? 'border-danger bg-danger-soft text-danger'
            : 'border-border bg-surface text-ink-muted',
        )}
      >
        {icon}
      </span>

      <h1>{heading}</h1>

      <p className="m-0 text-ink-muted">{body}</p>

      {/*
        Two `lg` buttons side by side on a 320 px window drop below the 44 px
        target, so they stretch full width at the base tier and stand side by
        side from `--bp-sm`, where the line is wide enough for both.
      */}
      <div data-error-actions className="flex w-full flex-wrap gap-3">
        {actions}
      </div>
    </section>
  );
}

/**
 * The per-route document title, restored on the way out.
 *
 * It exists for tab identification and for a screen reader announcing the new
 * document — not for search, which never reaches this host: every route ships
 * `noindex, nofollow` and `robots.txt` disallows all.
 */
function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
